import {
  TypographyProps,
  Typography,
  Box,
  Divider,
  Stack,
  SxProps,
  Theme,
} from "@mui/material";
import DOMPurify from "isomorphic-dompurify";
import { useState } from "react";
import { ReadOnlyQuill } from "@/components/common/quill/readOnlyQuill";
import { useEffectOnce } from "@/hooks/misc/useEffectOnce";
import { formattedPrice } from "@/utils";
import { pluralize } from "@/utils";
import { MedspaMembership } from "@/views/online-store/onlineStoreView";

export default function MembershipCardDescription({
  membership: {
    description,
    billingInterval,
    pricePerBillingPeriod,
    initiationFeeValue,
    cancellationFeeValue,
    minimumBillingCycles,
  },
  truncateDescription = true,
  descriptionCustomTextVariant,
  perkCustomTextVariant,
  sx,
}: {
  membership: MedspaMembership;
  truncateDescription?: boolean;
  descriptionCustomTextVariant?: TypographyProps["variant"];
  perkCustomTextVariant?: TypographyProps["variant"];
  sx?: SxProps<Theme>;
}) {
  // There is a hydration error caused by dangerouslySetInnerHTML when the online-store is initialized on memberships page
  // this useEffectOnce is a workaround to ensure description is rendered on the client side
  const [showDescription, setShowDescription] = useState(false);
  useEffectOnce(true, () => {
    setShowDescription(true);
  });

  return (
    <>
      <Typography
        variant="labelDefault"
        sx={{
          textAlign: "left",
        }}
      >
        {pricePerBillingPeriod ? (
          <>
            ${pricePerBillingPeriod} /{billingInterval}
          </>
        ) : (
          "Price not available"
        )}
      </Typography>
      <Box
        sx={{
          minHeight: "50px",
          mt: 1,
          maxHeight: "60vh",
          overflowY: "auto",
        }}
      >
        {!description && (
          <Typography
            variant="paragraphSmall"
            sx={{
              color: "text.secondary",
              fontStyle: "italic",
            }}
          >
            No description available
          </Typography>
        )}

        {description && truncateDescription && (
          <Typography
            variant={descriptionCustomTextVariant || "paragraphSmall"}
            dangerouslySetInnerHTML={{
              __html: DOMPurify.sanitize(description),
            }}
            sx={{
              color: "text.secondary",

              WebkitLineClamp: {
                xs: 3,
                sm: 2,
              },

              textOverflow: "ellipsis",
              overflow: "hidden",
              display: "-webkit-box",
              WebkitBoxOrient: "vertical",
              ...sx,
            }}
          />
        )}
        {description && showDescription && !truncateDescription && (
          <ReadOnlyQuill value={description} />
        )}
      </Box>
      <Box sx={{ bgcolor: "white" }}>
        <Divider sx={{ mb: 1, mt: showDescription ? 1 : 0 }} />
        <Row
          label="Initiation fee"
          value={formattedPrice(initiationFeeValue)}
          customTextVariant={perkCustomTextVariant}
        />
        <Row
          label="Early termination fee"
          value={formattedPrice(cancellationFeeValue)}
          customTextVariant={perkCustomTextVariant}
        />
        <Row
          label="Min subscription duration"
          value={
            minimumBillingCycles
              ? `${minimumBillingCycles} ${pluralize(
                  billingInterval,
                  minimumBillingCycles
                )}`
              : "None"
          }
          customTextVariant={perkCustomTextVariant}
        />
      </Box>
    </>
  );
}

function Row({
  label,
  value,
  customTextVariant,
}: {
  label: string;
  value: string;
  customTextVariant?: TypographyProps["variant"];
}) {
  return (
    <Stack
      direction="row"
      sx={{
        justifyContent: "space-between",
        alignItems: "center",
      }}
    >
      <Typography variant={customTextVariant || "labelTiny"}>
        {label}
      </Typography>
      <Typography variant={customTextVariant || "labelTiny"}>
        {value}
      </Typography>
    </Stack>
  );
}
